/**
 * Validation Utilities
 * Form validation helpers for the store-front application
 */

/**
 * Validates UUID format for access tokens
 */
export const isValidAccessToken = (token: string): boolean => {
    return /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/.test(token);
};

/**
 * Validates email format
 */
export const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validates phone number format (10 digits after stripping)
 */
export const validatePhoneNumber = (phoneNumber: string): boolean => {
    if (!phoneNumber) return true; // Phone is optional

    // Check for letters
    const lettersInString = phoneNumber.replace(/[^a-zA-Z ]/g, '').replace(/\s/g, '');
    if (lettersInString.length > 0) return false;

    const stripped = stripPhoneNumber(phoneNumber);
    return /^\s*([0-9]{10})\s*$/.test(stripped);
};

/**
 * Strips formatting from phone number
 */
export const stripPhoneNumber = (phoneNumber: string): string => {
    return phoneNumber.replace(/\(|\)/g, '').replace(/[\s+-]/g, '');
};

/**
 * Validates required field is not empty
 */
export const isRequired = (value: string): boolean => {
    return value.trim().length > 0;
};

/**
 * Validates zip code (US format - 5 or 9 digits)
 */
export const isValidZipCode = (zipCode: string): boolean => {
    return /^\d{5}(-\d{4})?$/.test(zipCode);
};

/**
 * Sanitizes access token input - removes invalid characters
 */
export const sanitizeAccessToken = (input: string): string => {
    return input.replace(/[^a-zA-Z0-9-]/g, '');
};

/**
 * Sanitizes pin input - removes non-alphanumeric characters
 */
export const sanitizePin = (input: string): string => {
    return input.replace(/[^a-zA-Z0-9]/g, '');
};

/**
 * Form validation rules for react-hook-form
 */
export const validationRules = {
    accessToken: {
        required: 'Access Code is required',
        validate: (value: string) => isValidAccessToken(value) || 'Access code has an invalid format',
    },
    pin: {
        required: 'Passcode is required',
    },
    firstName: {
        required: 'First name is required',
    },
    lastName: {
        required: 'Last name is required',
    },
    email: {
        required: 'Email is required',
        validate: (value: string) => isValidEmail(value) || 'Invalid email format',
    },
    zipCode: {
        required: 'Zip code is required',
        validate: (value: string) => isValidZipCode(value) || 'Zip code must be 5 digits (or 9-digit ZIP+4)',
    },
    phoneNumber: {
        validate: (value: string) => !value || validatePhoneNumber(value) || 'Invalid phone number',
    },
};
